// Shared JSON-LD schema.org builders for SEO/AEO structured data.
// Keep rendering concerns in components/StructuredData.js and page-specific
// content in callers so these helpers stay reusable across page types.

export const SITE_URL = 'https://campus.edu'
export const ORGANIZATION_ID = `${SITE_URL}/#organization`
export const WEBSITE_ID = `${SITE_URL}/#website`

function normalizeSiteUrl(siteUrl = SITE_URL) {
  return siteUrl.replace(/\/$/, '')
}

export function serializeStructuredData(data) {
  return JSON.stringify(data).replaceAll('<', '\\u003c')
}

export function absoluteUrl(path = '/', siteUrl = SITE_URL) {
  if (/^https?:\/\//i.test(path)) {
    return path
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${normalizeSiteUrl(siteUrl)}${normalizedPath}`
}

export function getSchemaGraph(nodes) {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes.filter(Boolean),
  }
}

export function getOrganizationSchema({logoUrl = '/images/logo.svg'} = {}) {
  return {
    '@type': 'CollegeOrUniversity',
    '@id': ORGANIZATION_ID,
    name: 'Campus',
    legalName: 'CampusGroup, Inc.',
    alternateName: 'Campus.edu',
    url: `${SITE_URL}/`,
    ...(logoUrl ? {logo: absoluteUrl(logoUrl)} : {}),
    description:
      'Campus is an online college with professors who also teach at top universities. Career-focused associate degree and certificate programs.',
    telephone: '+1-888-675-2460',
    email: 'hello@campus.edu',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '5221 Madison Ave',
      addressLocality: 'Sacramento',
      addressRegion: 'CA',
      postalCode: '95841',
      addressCountry: 'US',
    },
    sameAs: [
      'https://www.instagram.com/campus/',
      'https://www.linkedin.com/company/campusorg/',
      'https://www.facebook.com/campusgrad/',
      'https://www.youtube.com/channel/UC4BqIWM76iiuHWCBvqWuMEw',
      'https://twitter.com/Campusgrad',
      'https://www.tiktok.com/@campus',
    ],
  }
}

export function getWebsiteSchema({siteUrl = SITE_URL, name = 'Campus'} = {}) {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl)

  return {
    '@type': 'WebSite',
    '@id': `${normalizedSiteUrl}/#website`,
    url: `${normalizedSiteUrl}/`,
    name,
    publisher: {'@id': ORGANIZATION_ID},
  }
}

export function getWebPageSchema({
  path,
  name,
  description,
  pageType = 'WebPage',
  siteUrl = SITE_URL,
  id = `${absoluteUrl(path, siteUrl)}#webpage`,
  websiteId = WEBSITE_ID,
  about = {'@id': ORGANIZATION_ID},
  mainEntity,
} = {}) {
  const url = absoluteUrl(path, siteUrl)

  return {
    '@type': pageType,
    '@id': id,
    url,
    name,
    ...(description ? {description} : {}),
    isPartOf: {'@id': websiteId},
    ...(about ? {about} : {}),
    ...(mainEntity ? {mainEntity} : {}),
  }
}

export function getBreadcrumbSchema(items, siteUrl = SITE_URL) {
  if (!items?.length) {
    return null
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path, siteUrl),
    })),
  }
}

export function getFaqSchema(faqs, extra = {}) {
  if (!faqs?.length) {
    return null
  }

  return {
    '@type': 'FAQPage',
    ...extra,
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

export function getItemListSchema(items) {
  if (!items?.length) {
    return null
  }

  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item,
    })),
  }
}

export function getHowToSchema({
  name,
  description,
  steps,
  siteUrl = SITE_URL,
} = {}) {
  if (!steps?.length) {
    return null
  }

  return {
    '@type': 'HowTo',
    name,
    ...(description ? {description} : {}),
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.url ? {url: absoluteUrl(step.url, siteUrl)} : {}),
    })),
  }
}

export function getPersonItemListSchema(people, siteUrl = SITE_URL) {
  if (!people?.length) {
    return null
  }

  return getItemListSchema(
    people.map(person => ({
      '@type': 'Person',
      name: person.name,
      ...(person.url ? {url: absoluteUrl(person.url, siteUrl)} : {}),
      ...(person.jobTitle ? {jobTitle: person.jobTitle} : {}),
      worksFor: {'@id': ORGANIZATION_ID},
      ...(person.affiliation
        ? {
            affiliation: {
              '@type': 'CollegeOrUniversity',
              name: person.affiliation,
            },
          }
        : {}),
    })),
  )
}

export function getSitePageSchema({
  path,
  title,
  description,
  breadcrumbs,
  faqs,
  pageType = 'WebPage',
  organization = getOrganizationSchema(),
  siteUrl = SITE_URL,
  websiteName = 'Campus',
  extraNodes = [],
} = {}) {
  const url = absoluteUrl(path, siteUrl)
  const websiteId = `${normalizeSiteUrl(siteUrl)}/#website`
  const faqSchema = getFaqSchema(faqs, {
    '@id': `${url}#faq`,
    url,
    name: `${title} FAQ`,
    isPartOf: {'@id': websiteId},
    about: {'@id': ORGANIZATION_ID},
  })

  return getSchemaGraph([
    organization,
    getWebsiteSchema({siteUrl, name: websiteName}),
    getWebPageSchema({
      path,
      name: title,
      description,
      pageType,
      siteUrl,
      websiteId,
      ...(faqSchema ? {mainEntity: {'@id': faqSchema['@id']}} : {}),
    }),
    faqSchema,
    ...extraNodes,
    getBreadcrumbSchema(breadcrumbs, siteUrl),
  ])
}

export function getEducationalProgramSchema(program, siteUrl = SITE_URL) {
  if (!program) {
    return null
  }

  const url = absoluteUrl(program.path, siteUrl)

  return {
    '@type': 'EducationalOccupationalProgram',
    '@id': `${url}#program`,
    name: program.name,
    url,
    provider: {'@id': ORGANIZATION_ID},
    ...(program.description ? {description: program.description} : {}),
    ...(program.credentialAwarded
      ? {educationalCredentialAwarded: program.credentialAwarded}
      : {}),
    ...(program.timeToComplete ? {timeToComplete: program.timeToComplete} : {}),
    ...(program.programType ? {programType: program.programType} : {}),
    ...(program.educationalProgramMode
      ? {educationalProgramMode: program.educationalProgramMode}
      : {}),
    ...(program.offers
      ? {
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            ...program.offers,
          },
        }
      : {}),
  }
}

export function getProgramPageSchema(
  program,
  breadcrumbs,
  {siteUrl = SITE_URL, websiteName = 'Campus'} = {},
) {
  if (!program) {
    return null
  }

  const url = absoluteUrl(program.path, siteUrl)
  const websiteId = `${normalizeSiteUrl(siteUrl)}/#website`
  const faqSchema = getFaqSchema(program.faqs, {
    '@id': `${url}#faq`,
    url,
    name: `${program.name} Program Courses FAQ`,
    isPartOf: {'@id': websiteId},
    about: {'@id': `${url}#program`},
  })

  return getSchemaGraph([
    getOrganizationSchema(),
    getWebsiteSchema({siteUrl, name: websiteName}),
    getWebPageSchema({
      path: program.path,
      name: program.title || program.name,
      description: program.description,
      siteUrl,
      websiteId,
      mainEntity: {'@id': `${url}#program`},
    }),
    getEducationalProgramSchema(program, siteUrl),
    faqSchema,
    getBreadcrumbSchema(
      breadcrumbs || [
        {name: 'Home', path: '/'},
        {name: 'Programs', path: '/programs'},
        {name: program.name, path: program.path},
      ],
      siteUrl,
    ),
  ])
}

export function getProgramListingSchema({path, title, description, programs}) {
  const programItems =
    programs?.map(program => getEducationalProgramSchema(program)) || []

  return getSchemaGraph([
    getOrganizationSchema(),
    getWebsiteSchema(),
    getWebPageSchema({
      path,
      name: title,
      description,
      pageType: 'CollectionPage',
    }),
    programItems.length ? getItemListSchema(programItems) : null,
    getBreadcrumbSchema([
      {name: 'Home', path: '/'},
      {name: 'Programs', path},
    ]),
  ])
}

export function getBlogPostPath(post) {
  if (post.tags?.[0]?.slug) {
    return `/blog/${post.tags[0].slug}/${post.slug}`
  }

  return `/blog/${post.slug}`
}

export function getBlogListingSchema({
  path,
  title,
  description,
  posts,
  activeTag,
  author,
} = {}) {
  const url = absoluteUrl(path)
  const listItems =
    posts?.map(post => ({
      '@type': 'BlogPosting',
      '@id': `${absoluteUrl(getBlogPostPath(post))}#article`,
      headline: post.title,
      url: absoluteUrl(getBlogPostPath(post)),
    })) || []

  const listingNode = author
    ? getWebPageSchema({
        path,
        name: title,
        description,
        pageType: 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: author.name,
          ...(author.slug
            ? {url: absoluteUrl(`/blog/author/${author.slug}`)}
            : {}),
        },
      })
    : activeTag
      ? getWebPageSchema({
          path,
          name: title,
          description,
          pageType: 'CollectionPage',
        })
      : {
          '@type': 'Blog',
          '@id': `${url}#blog`,
          url,
          name: title,
          description,
          isPartOf: {'@id': WEBSITE_ID},
          publisher: {'@id': ORGANIZATION_ID},
          ...(listItems.length ? {blogPost: listItems} : {}),
        }

  return getSchemaGraph([
    getOrganizationSchema(),
    getWebsiteSchema(),
    listingNode,
    listItems.length
      ? getItemListSchema(listItems.map(item => ({'@id': item['@id']})))
      : null,
    getBreadcrumbSchema([
      {name: 'Home', path: '/'},
      {name: 'Blog', path: '/blog'},
      ...(author
        ? [{name: author.name, path}]
        : activeTag
          ? [{name: activeTag.name, path}]
          : []),
    ]),
  ])
}

export function getBlogPostSchema(post) {
  if (!post) {
    return null
  }

  const path = getBlogPostPath(post)
  const url = absoluteUrl(path)
  const author = post.primary_author || post.authors?.[0]
  const image = post.feature_image ? absoluteUrl(post.feature_image) : undefined

  return getSchemaGraph([
    getOrganizationSchema(),
    getWebsiteSchema(),
    getWebPageSchema({
      path,
      name: post.meta_title || post.title,
      description: post.meta_description || post.excerpt,
      mainEntity: {'@id': `${url}#article`},
    }),
    {
      '@type': 'BlogPosting',
      '@id': `${url}#article`,
      headline: post.title,
      ...(post.meta_title && post.meta_title !== post.title
        ? {alternativeHeadline: post.meta_title}
        : {}),
      ...(post.meta_description || post.excerpt
        ? {description: post.meta_description || post.excerpt}
        : {}),
      url,
      ...(image ? {image} : {}),
      ...(post.published_at ? {datePublished: post.published_at} : {}),
      ...(post.updated_at ? {dateModified: post.updated_at} : {}),
      author: author
        ? {
            '@type': 'Person',
            name: author.name,
            ...(author.slug
              ? {url: absoluteUrl(`/blog/author/${author.slug}`)}
              : {}),
          }
        : {'@id': ORGANIZATION_ID},
      publisher: {'@id': ORGANIZATION_ID},
      mainEntityOfPage: {'@id': `${url}#webpage`},
      ...(post.primary_tag?.name
        ? {articleSection: post.primary_tag.name}
        : {}),
      ...(post.tags?.length ? {keywords: post.tags.map(tag => tag.name)} : {}),
    },
    getBreadcrumbSchema([
      {name: 'Home', path: '/'},
      {name: 'Blog', path: '/blog'},
      ...(post.primary_tag
        ? [
            {
              name: post.primary_tag.name,
              path: `/blog/${post.primary_tag.slug}`,
            },
          ]
        : []),
      {name: post.title, path},
    ]),
  ])
}
